/**
 * Admin → Founder Dashboard (iter 87)
 *
 * One unified board for cross-program metrics rolled up across all 4
 * flagship programs. Combines the user-provided Longevity (12 metrics)
 * and Precision Health Membership (14 metrics) dashboard specs into a
 * single operator view.
 *
 * Source: GET /api/admin/founder-dashboard
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  BadgePercent,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  Loader2,
  RefreshCw,
  Repeat,
  TrendingUp,
  Users,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function fmtCents(c) {
  const v = (c || 0) / 100;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtPct(p) {
  return `${((p || 0) * 100).toFixed(1)}%`;
}

function MetricCard({ label, value, sub, icon: Icon, accent = "blue", testid }) {
  const palette = {
    blue: "border-blue-500/30 bg-blue-950/20 text-blue-200",
    emerald: "border-emerald-500/30 bg-emerald-950/20 text-emerald-200",
    amber: "border-amber-500/30 bg-amber-950/20 text-amber-200",
    rose: "border-rose-500/30 bg-rose-950/20 text-rose-200",
    violet: "border-violet-500/30 bg-violet-950/20 text-violet-200",
    slate: "border-slate-700 bg-slate-900/40 text-slate-200",
  }[accent];
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-4 ${palette}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
          {label}
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-100">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminFounderDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/founder-dashboard");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = data?.totals || {};
  const programs = data?.programs || [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Admin · Founder Dashboard
            </div>
            <h1 className="mt-1 text-3xl font-bold">Cross-program metrics</h1>
            <p className="mt-2 text-sm text-slate-400 max-w-3xl">
              Unified roll-up across all 4 flagship programs — combines Longevity-style
              per-program metrics (enrolled, revenue, margin, completion rates,
              stalled orders) with Precision Health Membership recurring metrics
              (active members, MRR/ARR/ARPM, renewal/churn/failed-payment).
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="founder-dashboard-refresh"
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Crunching metrics…
          </div>
        ) : (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="founder-dashboard-totals">
              <MetricCard
                label="Total revenue"
                value={fmtCents(totals.program_revenue_cents)}
                icon={CircleDollarSign}
                accent="emerald"
                sub={`${(totals.one_time_paid_count || 0) + (totals.recurring_paid_count || 0)} paid checkouts`}
                testid="founder-metric-total-revenue"
              />
              <MetricCard
                label="MRR"
                value={fmtCents(totals.mrr_cents)}
                icon={Repeat}
                accent="violet"
                sub={`ARR ${fmtCents(totals.arr_cents)}`}
                testid="founder-metric-mrr"
              />
              <MetricCard
                label="Active members"
                value={(totals.active_members || 0).toLocaleString()}
                icon={Users}
                accent="blue"
                sub={`ARPM ${fmtCents(totals.arpm_cents)}`}
                testid="founder-metric-active-members"
              />
              <MetricCard
                label="Patients enrolled"
                value={(totals.patients_enrolled || 0).toLocaleString()}
                icon={Activity}
                accent="amber"
                sub={`${totals.stalled_orders || 0} stalled (past SLA)`}
                testid="founder-metric-enrolled"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <MetricCard
                label="Clinic revenue (gross)"
                value={fmtCents(totals.clinic_revenue_cents)}
                icon={TrendingUp}
                accent="emerald"
                testid="founder-metric-clinic-revenue"
              />
              <MetricCard
                label="Nodera fees collected"
                value={fmtCents(totals.nodera_fees_cents)}
                icon={BadgePercent}
                accent="blue"
                testid="founder-metric-nodera-fees"
              />
              <MetricCard
                label="One-time paid"
                value={(totals.one_time_paid_count || 0).toLocaleString()}
                icon={CheckCircle2}
                accent="slate"
                testid="founder-metric-one-time"
              />
              <MetricCard
                label="Recurring paid"
                value={(totals.recurring_paid_count || 0).toLocaleString()}
                icon={CalendarClock}
                accent="slate"
                testid="founder-metric-recurring"
              />
            </div>

            {/* Per-program breakdown */}
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              Per-program breakdown
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden mb-8">
              <table className="w-full text-sm" data-testid="founder-dashboard-table">
                <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Program</th>
                    <th className="text-right px-4 py-3">Enrolled</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                    <th className="text-right px-4 py-3">MRR</th>
                    <th className="text-right px-4 py-3">Lab compl.</th>
                    <th className="text-right px-4 py-3">Membership conv.</th>
                    <th className="text-right px-4 py-3">Stalled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {programs.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-center text-slate-500" colSpan="7">
                        No programs found.
                      </td>
                    </tr>
                  ) : (
                    programs.map((p) => (
                      <tr key={p.program_id} data-testid={`founder-dashboard-row-${p.slug}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold text-slate-100">{p.name}</div>
                            {p.is_recurring && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                                <Repeat className="w-2.5 h-2.5" /> Recurring
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">{p.slug}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">{p.patients_enrolled}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {fmtCents(p.program_revenue_cents)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {fmtCents(p.mrr_cents)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {fmtPct(p.lab_completion_rate)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {fmtPct(p.membership_conversion_rate)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-mono ${
                            p.stalled_orders > 0 ? "text-rose-300" : "text-slate-200"
                          }`}
                        >
                          {p.stalled_orders}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Recurring-specific row (Precision spec) */}
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              Recurring-program health
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
              <table className="w-full text-sm" data-testid="founder-recurring-table">
                <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Program</th>
                    <th className="text-right px-4 py-3">Active members</th>
                    <th className="text-right px-4 py-3">MRR</th>
                    <th className="text-right px-4 py-3">ARR</th>
                    <th className="text-right px-4 py-3">ARPM</th>
                    <th className="text-right px-4 py-3">Renewal</th>
                    <th className="text-right px-4 py-3">Churn</th>
                    <th className="text-right px-4 py-3">Failed pmt.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {programs.filter((p) => p.is_recurring).length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-center text-slate-500" colSpan="8">
                        No recurring programs found.
                      </td>
                    </tr>
                  ) : (
                    programs
                      .filter((p) => p.is_recurring)
                      .map((p) => (
                        <tr key={p.program_id}>
                          <td className="px-4 py-3 font-semibold text-slate-100">{p.name}</td>
                          <td className="px-4 py-3 text-right font-mono">{p.active_members}</td>
                          <td className="px-4 py-3 text-right font-mono">{fmtCents(p.mrr_cents)}</td>
                          <td className="px-4 py-3 text-right font-mono">{fmtCents(p.arr_cents)}</td>
                          <td className="px-4 py-3 text-right font-mono">{fmtCents(p.arpm_cents)}</td>
                          <td className="px-4 py-3 text-right font-mono">{fmtPct(p.renewal_rate)}</td>
                          <td
                            className={`px-4 py-3 text-right font-mono ${
                              p.churn_rate > 0.05 ? "text-rose-300" : "text-slate-200"
                            }`}
                          >
                            {fmtPct(p.churn_rate)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-mono ${
                              p.failed_payment_rate > 0.02 ? "text-amber-300" : "text-slate-200"
                            }`}
                          >
                            {fmtPct(p.failed_payment_rate)}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-[11px] text-slate-500">
              Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
